import AwesomeDebouncePromise from 'awesome-debounce-promise'
import { useMemo } from 'react'

import { useCompanies } from './use-companies'
import { CompanyOption } from '../models'

interface UseFilteredCompanyOptions {
  fetchFilteredCompanies: (filterString: string, maxDisplayedResultCount?: number) => Promise<CompanyOption[]>
}

/**
 * Hook that returns a function to get company options filtered by a given string inside the BE
 * @param filterString
 * @returns
 */
export const useFilteredCompanyOptions = (): UseFilteredCompanyOptions => {
  const { getCompaniesConnected } = useCompanies()

  // Backend filter values with commas are quote-delimited; embedded quotes must be doubled.
  const escapeCompanyNameFilterValue = (value: string): string => value.replace(/\\/g, '\\\\').replace(/"/g, '""')

  const getFilteredCompanyOptions = (
    filterString: string,
    maxDisplayedResultCount?: number
  ): Promise<{ value: number; inputLabel: string; optionLabel: string }[]> => {
    /**
     * this is a workaround to be able to search companies with a comma in their name.
     * Since only one company is filtered at a time it does not break
     * */
    const escapedFilterString = escapeCompanyNameFilterValue(filterString)
    const filter = filterString.includes(',') ? `firma.Name="*${escapedFilterString}*"` : `firma.Name=*${escapedFilterString}*`
    const companies = getCompaniesConnected({ filterOr: filter })
      .then(result => {
        const sliced = result.slice(0, maxDisplayedResultCount).map(company => ({
          inputLabel: company.companyName ?? '',
          optionLabel: `${company.companyName ?? ''} - ${company.locationAddress ?? ''}, ${company.locationPlaceOr ?? ''}`,
          value: company.companyId,
        }))
        return sliced
      })
      .catch(error => {
        console.error({ error })
        return []
      })

    return companies
  }

  const fetchFilteredCompanies = useMemo(
    () => AwesomeDebouncePromise(getFilteredCompanyOptions, 500),
    [] //eslint-disable-line
  )

  return { fetchFilteredCompanies }
}
